import React, { useState, useEffect } from 'react';
import { Save, X, Clock, Plus, Trash2, Copy } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { getStaffFullName } from '../../../../utils/staffUtils';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

const AvailabilityScheduler = ({ staff, onSave, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [error, setError] = useState('');
  const [schedules, setSchedules] = useState({});
  const [copyFromDay, setCopyFromDay] = useState('');

  useEffect(() => {
    if (staff?.id) {
      loadAvailability();
    }
  }, [staff]);

  const loadAvailability = async () => {
    try {
      setLoadingSchedule(true);
      const { data, error } = await supabase
        .from('staff_availability')
        .select('*')
        .eq('staff_id', staff.id)
        .order('day_of_week');

      if (error) throw error;

      // Convert array to object keyed by day_of_week
      const schedulesObj = {};
      data?.forEach(schedule => {
        schedulesObj[schedule.day_of_week] = {
          id: schedule.id,
          isAvailable: schedule.is_available,
          startTime: schedule.start_time?.slice(0, 5) || '09:00',
          endTime: schedule.end_time?.slice(0, 5) || '17:00',
          breakStartTime: schedule.break_start_time?.slice(0, 5) || '',
          breakEndTime: schedule.break_end_time?.slice(0, 5) || '',
          slotDuration: schedule.slot_duration_minutes || 30,
          maxBookings: schedule.max_bookings_per_slot || 1,
          notes: schedule.notes || ''
        };
      });

      setSchedules(schedulesObj);
    } catch (err) {
      setError('Failed to load availability schedule');
      console.error('Error loading availability:', err);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleDayToggle = (dayValue) => {
    setSchedules(prev => ({
      ...prev,
      [dayValue]: prev[dayValue]
        ? { ...prev[dayValue], isAvailable: !prev[dayValue].isAvailable }
        : {
          isAvailable: true,
          startTime: '09:00',
          endTime: '17:00',
          breakStartTime: '13:00',
          breakEndTime: '14:00',
          slotDuration: 30,
          maxBookings: 1,
          notes: ''
        }
    }));
  };

  const handleScheduleChange = (dayValue, field, value) => {
    setSchedules(prev => ({
      ...prev,
      [dayValue]: {
        ...prev[dayValue],
        [field]: value
      }
    }));
  };

  const handleCopySchedule = () => {
    if (!copyFromDay || !schedules[copyFromDay]) return;

    const sourceSchedule = schedules[copyFromDay];
    const newSchedules = { ...schedules };

    DAYS_OF_WEEK.forEach(day => {
      if (day.value !== parseInt(copyFromDay)) {
        newSchedules[day.value] = {
          ...newSchedules[day.value],
          ...sourceSchedule,
          id: newSchedules[day.value]?.id // Preserve existing ID if any
        };
      }
    });

    setSchedules(newSchedules);
    setCopyFromDay('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Delete all existing schedules for this staff member
      await supabase
        .from('staff_availability')
        .delete()
        .eq('staff_id', staff.id);

      // Insert new schedules
      const schedulesToInsert = Object.entries(schedules)
        .filter(([_, schedule]) => schedule.isAvailable)
        .map(([dayValue, schedule]) => ({
          staff_id: staff.id,
          day_of_week: parseInt(dayValue),
          start_time: schedule.startTime + ':00',
          end_time: schedule.endTime + ':00',
          break_start_time: schedule.breakStartTime ? schedule.breakStartTime + ':00' : null,
          break_end_time: schedule.breakEndTime ? schedule.breakEndTime + ':00' : null,
          slot_duration_minutes: parseInt(schedule.slotDuration),
          max_bookings_per_slot: parseInt(schedule.maxBookings),
          is_available: true,
          notes: schedule.notes || null
        }));

      if (schedulesToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('staff_availability')
          .insert(schedulesToInsert);

        if (insertError) throw insertError;
      }

      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save availability schedule');
      console.error('Error saving availability:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingSchedule) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Staff Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h3 className="font-semibold text-purple-800 mb-1">
          {getStaffFullName(staff?.fullName, staff?.role, staff?.gender)}
        </h3>
        <p className="text-sm text-purple-600">
          {staff?.role?.charAt(0).toUpperCase() + staff?.role?.slice(1)}
        </p>
      </div>

      {/* Copy Schedule Tool */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
          <Copy className="w-5 h-5" />
          Quick Copy Schedule
        </h4>
        <div className="flex gap-3">
          <select
            value={copyFromDay}
            onChange={(e) => setCopyFromDay(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Select a day to copy from...</option>
            {DAYS_OF_WEEK.filter(day => schedules[day.value]?.isAvailable).map(day => (
              <option key={day.value} value={day.value}>{day.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleCopySchedule}
            disabled={!copyFromDay}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Copy to All Days
          </button>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Copy the schedule from one day to all other days
        </p>
      </div>

      {/* Weekly Schedule */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Weekly Schedule</h3>

        {DAYS_OF_WEEK.map(day => {
          const schedule = schedules[day.value] || {};
          const isAvailable = schedule.isAvailable || false;

          return (
            <div key={day.value} className="border border-gray-200 rounded-lg p-4">
              {/* Day Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isAvailable}
                    onChange={() => handleDayToggle(day.value)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <label className="font-medium text-gray-800">{day.label}</label>
                </div>
                {isAvailable && (
                  <span className="text-sm text-green-600 font-medium">Available</span>
                )}
              </div>

              {/* Schedule Details */}
              {isAvailable && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={schedule.startTime || '09:00'}
                      onChange={(e) => handleScheduleChange(day.value, 'startTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1" />
                      End Time
                    </label>
                    <input
                      type="time"
                      value={schedule.endTime || '17:00'}
                      onChange={(e) => handleScheduleChange(day.value, 'endTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slot Duration (min)
                    </label>
                    <select
                      value={schedule.slotDuration || 30}
                      onChange={(e) => handleScheduleChange(day.value, 'slotDuration', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">60 minutes</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Break Start
                    </label>
                    <input
                      type="time"
                      value={schedule.breakStartTime || ''}
                      onChange={(e) => handleScheduleChange(day.value, 'breakStartTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Break End
                    </label>
                    <input
                      type="time"
                      value={schedule.breakEndTime || ''}
                      onChange={(e) => handleScheduleChange(day.value, 'breakEndTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Bookings/Slot
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={schedule.maxBookings || 1}
                      onChange={(e) => handleScheduleChange(day.value, 'maxBookings', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <input
                      type="text"
                      value={schedule.notes || ''}
                      onChange={(e) => handleScheduleChange(day.value, 'notes', e.target.value)}
                      placeholder="e.g., Emergency consultations only"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">Schedule Summary</h4>
        <p className="text-sm text-blue-700">
          {Object.values(schedules).filter(s => s.isAvailable).length} days configured
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
        >
          <X className="w-5 h-5" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Availability
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default AvailabilityScheduler;

